// TikTok Scraper Integration Component
// Handles user input for TikTok scraping and manages the scraping process
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/hooks/use-toast'
import { Loader2, Video, Hash, User, AlertCircle, DollarSign } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/integrations/supabase/client'

interface TikTokScrapeConfig {
  profiles: string[]
  hashtags: string[]
  videoUrls: string[]
  maxItems: number
  shouldDownloadVideos: boolean
  shouldDownloadSubtitles: boolean
  shouldDownloadCovers: boolean
  positId: string
  processTranscripts: boolean
  analyzeScreenshots: boolean
  enableSpeakerDetection: boolean
  speakerValidationMode: 'auto' | 'suggest' | 'manual'
}

interface ScrapeProgress {
  status: 'idle' | 'scraping' | 'processing' | 'validating' | 'complete' | 'error'
  currentStep: string
  itemsProcessed: number
  totalItems: number
  cost: number
  errors: string[]
}

export function TikTokScraperIntegration() {
  const { user } = useAuth()
  const { toast } = useToast()

  const [activeTab, setActiveTab] = useState('profile')
  const [isScraping, setIsScraping] = useState(false)
  const [progress, setProgress] = useState<ScrapeProgress>({
    status: 'idle',
    currentStep: '',
    itemsProcessed: 0,
    totalItems: 0,
    cost: 0,
    errors: []
  })

  // Form state
  const [config, setConfig] = useState<TikTokScrapeConfig>({
    profiles: [],
    hashtags: [],
    videoUrls: [],
    maxItems: 50,
    shouldDownloadVideos: false,
    shouldDownloadSubtitles: false,
    shouldDownloadCovers: true,
    positId: '',
    processTranscripts: true,
    analyzeScreenshots: false,
    enableSpeakerDetection: true,
    speakerValidationMode: 'suggest'
  })

  // Input values for different tabs
  const [profileInput, setProfileInput] = useState('')
  const [hashtagInput, setHashtagInput] = useState('')
  const [videoUrlInput, setVideoUrlInput] = useState('')

  const updateConfig = (key: keyof TikTokScrapeConfig, value: any) => {
    setConfig(prev => ({ ...prev, [key]: value }))
  }

  const addProfile = () => {
    if (!profileInput.trim()) return
    const profiles = [...config.profiles]
    if (!profiles.includes(profileInput.trim())) {
      profiles.push(profileInput.trim())
      updateConfig('profiles', profiles)
    }
    setProfileInput('')
  }

  const addHashtag = () => {
    if (!hashtagInput.trim()) return
    const hashtags = [...config.hashtags]
    if (!hashtags.includes(hashtagInput.trim())) {
      hashtags.push(hashtagInput.trim())
      updateConfig('hashtags', hashtags)
    }
    setHashtagInput('')
  }

  const addVideoUrl = () => {
    if (!videoUrlInput.trim()) return
    const videoUrls = [...config.videoUrls]
    if (!videoUrls.includes(videoUrlInput.trim())) {
      videoUrls.push(videoUrlInput.trim())
      updateConfig('videoUrls', videoUrls)
    }
    setVideoUrlInput('')
  }

  const removeItem = (type: 'profiles' | 'hashtags' | 'videoUrls', index: number) => {
    const items = [...config[type]]
    items.splice(index, 1)
    updateConfig(type, items)
  }

  const calculateEstimatedCost = () => {
    const baseCost = 0.03 // Actor start
    const itemCost = 0.004 // Per item
    const videoCost = config.shouldDownloadVideos ? 0.001 : 0

    const totalItems = Math.min(
      config.profiles.length + config.hashtags.length + config.videoUrls.length,
      config.maxItems
    )

    return baseCost + (totalItems * itemCost) + (totalItems * videoCost)
  }

  const startScraping = async () => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please log in to use TikTok integration.",
        variant: "destructive"
      })
      return
    }

    if (!config.positId) {
      toast({
        title: "Posit Required",
        description: "Please select a Posit to import the content to.",
        variant: "destructive"
      })
      return
    }

    // Validate that at least one target is specified
    if (config.profiles.length === 0 && config.hashtags.length === 0 && config.videoUrls.length === 0) {
      toast({
        title: "No Targets Specified",
        description: "Please add at least one TikTok profile, hashtag, or video URL.",
        variant: "destructive"
      })
      return
    }

    setIsScraping(true)
    setProgress({
      status: 'scraping',
      currentStep: 'Initializing TikTok scraper...',
      itemsProcessed: 0,
      totalItems: 0,
      cost: 0,
      errors: []
    })

    try {
      const { data, error } = await supabase.functions.invoke('scrape-tiktok-v1', {
        body: {
          config: {
            ...config,
            userId: user.id
          }
        }
      })

      if (error) throw error

      // Update progress based on response
      setProgress(prev => ({
        ...prev,
        status: data.success ? 'complete' : 'error',
        itemsProcessed: data.itemsProcessed || 0,
        totalItems: data.itemsProcessed || 0,
        cost: data.cost || 0,
        errors: data.errors || []
      }))

      if (data.success) {
        toast({
          title: "TikTok Import Complete! 🎉",
          description: `Successfully imported ${data.itemsProcessed} items to your Posit.`,
        })
      } else {
        throw new Error(data.errors?.[0] || 'Scraping failed')
      }

    } catch (error) {
      console.error('Scraping error:', error)
      setProgress(prev => ({
        ...prev,
        status: 'error',
        errors: [error instanceof Error ? error.message : 'Unknown error occurred']
      }))

      toast({
        title: "Scraping Failed",
        description: error instanceof Error ? error.message : "An error occurred during scraping.",
        variant: "destructive"
      })
    } finally {
      setIsScraping(false)
    }
  }

  const estimatedCost = calculateEstimatedCost()

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Video className="h-5 w-5" />
            TikTok Content Scraper
          </CardTitle>
          <CardDescription>
            Import TikTok videos, profiles, and hashtags into your Posit
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Progress Display */}
          {progress.status !== 'idle' && (
            <Card className="border-muted">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    {progress.status === 'scraping' && <Loader2 className="h-4 w-4 animate-spin" />}
                    {progress.status === 'processing' && <Loader2 className="h-4 w-4 animate-spin" />}
                    {progress.status === 'complete' && <div className="h-4 w-4 rounded-full bg-green-500" />}
                    {progress.status === 'error' && <AlertCircle className="h-4 w-4 text-red-500" />}
                    <span className="font-medium">{progress.currentStep}</span>
                  </div>
                  <Badge variant={
                    progress.status === 'complete' ? 'default' :
                    progress.status === 'error' ? 'destructive' : 'secondary'
                  }>
                    {progress.status}
                  </Badge>
                </div>

                {progress.totalItems > 0 && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Progress</span>
                      <span>{progress.itemsProcessed}/{progress.totalItems}</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className="bg-primary h-2 rounded-full transition-all duration-300"
                        style={{ width: `${(progress.itemsProcessed / progress.totalItems) * 100}%` }}
                      />
                    </div>
                  </div>
                )}

                {progress.errors.length > 0 && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                    <h4 className="text-sm font-medium text-red-800 mb-2">Errors:</h4>
                    <ul className="text-sm text-red-700 space-y-1">
                      {progress.errors.map((error, index) => (
                        <li key={index}>• {error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Input Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="profile" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Profile
              </TabsTrigger>
              <TabsTrigger value="hashtag" className="flex items-center gap-2">
                <Hash className="h-4 w-4" />
                Hashtag
              </TabsTrigger>
              <TabsTrigger value="video" className="flex items-center gap-2">
                <Video className="h-4 w-4" />
                Video URL
              </TabsTrigger>
            </TabsList>

            <TabsContent value="profile" className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="profile-input">TikTok Profile</Label>
                <div className="flex gap-2">
                  <Input
                    id="profile-input"
                    placeholder="@username or username"
                    value={profileInput}
                    onChange={(e) => setProfileInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addProfile()}
                  />
                  <Button onClick={addProfile} disabled={!profileInput.trim()}>
                    Add
                  </Button>
                </div>
              </div>

              {config.profiles.length > 0 && (
                <div className="space-y-2">
                  <Label>Added Profiles:</Label>
                  <div className="flex flex-wrap gap-2">
                    {config.profiles.map((profile, index) => (
                      <Badge key={index} variant="secondary" className="cursor-pointer"
                             onClick={() => removeItem('profiles', index)}>
                        @{profile} ×
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="hashtag" className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="hashtag-input">TikTok Hashtag</Label>
                <div className="flex gap-2">
                  <Input
                    id="hashtag-input"
                    placeholder="#hashtag or hashtag"
                    value={hashtagInput}
                    onChange={(e) => setHashtagInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addHashtag()}
                  />
                  <Button onClick={addHashtag} disabled={!hashtagInput.trim()}>
                    Add
                  </Button>
                </div>
              </div>

              {config.hashtags.length > 0 && (
                <div className="space-y-2">
                  <Label>Added Hashtags:</Label>
                  <div className="flex flex-wrap gap-2">
                    {config.hashtags.map((hashtag, index) => (
                      <Badge key={index} variant="secondary" className="cursor-pointer"
                             onClick={() => removeItem('hashtags', index)}>
                        #{hashtag} ×
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="video" className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="video-input">TikTok Video URL</Label>
                <div className="flex gap-2">
                  <Input
                    id="video-input"
                    placeholder="https://www.tiktok.com/@username/video/123..."
                    value={videoUrlInput}
                    onChange={(e) => setVideoUrlInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addVideoUrl()}
                  />
                  <Button onClick={addVideoUrl} disabled={!videoUrlInput.trim()}>
                    Add
                  </Button>
                </div>
              </div>

              {config.videoUrls.length > 0 && (
                <div className="space-y-2">
                  <Label>Added Video URLs:</Label>
                  <div className="space-y-2">
                    {config.videoUrls.map((url, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-muted rounded">
                        <span className="text-sm truncate">{url}</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeItem('videoUrls', index)}
                        >
                          ×
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>

          {/* Configuration Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="max-items">Maximum Items</Label>
              <Input
                id="max-items"
                type="number"
                min="1"
                max="1000"
                value={config.maxItems}
                onChange={(e) => updateConfig('maxItems', parseInt(e.target.value) || 50)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="posit-id">Posit ID</Label>
              <Input
                id="posit-id"
                placeholder="Enter Posit ID to import to"
                value={config.positId}
                onChange={(e) => updateConfig('positId', e.target.value)}
              />
            </div>
          </div>

          {/* Processing Options */}
          <div className="space-y-4">
            <h4 className="font-medium">Processing Options</h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="download-videos">Download Videos</Label>
                <Switch
                  id="download-videos"
                  checked={config.shouldDownloadVideos}
                  onCheckedChange={(checked) => updateConfig('shouldDownloadVideos', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="download-subtitles">Download Subtitles</Label>
                <Switch
                  id="download-subtitles"
                  checked={config.shouldDownloadSubtitles}
                  onCheckedChange={(checked) => updateConfig('shouldDownloadSubtitles', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="download-covers">Download Covers</Label>
                <Switch
                  id="download-covers"
                  checked={config.shouldDownloadCovers}
                  onCheckedChange={(checked) => updateConfig('shouldDownloadCovers', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="process-transcripts">Process Transcripts</Label>
                <Switch
                  id="process-transcripts"
                  checked={config.processTranscripts}
                  onCheckedChange={(checked) => updateConfig('processTranscripts', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="analyze-screenshots">Analyze Screenshots</Label>
                <Switch
                  id="analyze-screenshots"
                  checked={config.analyzeScreenshots}
                  onCheckedChange={(checked) => updateConfig('analyzeScreenshots', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="speaker-detection">Speaker Detection</Label>
                <Switch
                  id="speaker-detection"
                  checked={config.enableSpeakerDetection}
                  onCheckedChange={(checked) => updateConfig('enableSpeakerDetection', checked)}
                />
              </div>
            </div>
          </div>

          {/* Cost Estimation */}
          <Card className="bg-muted/50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  <span className="font-medium">Estimated Cost</span>
                </div>
                <span className="text-lg font-bold">${estimatedCost.toFixed(3)}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Based on {Math.min(config.profiles.length + config.hashtags.length + config.videoUrls.length, config.maxItems)} items
                {config.shouldDownloadVideos && ' (including video downloads)'}
              </p>
            </CardContent>
          </Card>

          {/* Start Scraping Button */}
          <Button
            onClick={startScraping}
            disabled={isScraping || !config.positId}
            className="w-full"
            size="lg"
          >
            {isScraping ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Scraping TikTok Content...
              </>
            ) : (
              <>
                <Video className="h-4 w-4 mr-2" />
                Start TikTok Import
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}

